import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiDelete, apiGet, apiPost, apiPut } from '../lib/api-client';
import { apiError } from '../lib/utils';
import { formatMoney, penceToPoundsInput, poundsToPence, vatPenceOnLines, vatRateValue } from '../lib/money';
import { loadedRecordKey, useEditGuard } from '../lib/dirty';
import { useLinkedPreview } from '../lib/linked-scroll';
import { useAuth } from '../contexts/AuthContext';
import { useNotice } from '../components/ui/Notice';
import { Alert } from '../components/ui/Badge';
import { RecordStepper } from '../components/records/RecordStepper';
import { PageHead, SectionCard } from '../components/ui/chrome';
import { Button } from '../components/ui/Button';
import { EditWithAiButton, RecordAssistDialog } from '../components/records/RecordAssistDialog';
import { Field, GrowingText, Input, Select, TextArea } from '../components/ui/Input';
import { LineTable } from '../components/documents/LineTable';
import { VatRateSelect } from '../components/documents/VatRateSelect';
import { EmailTemplateSelect, SupplierTemplateSelect, TermsTemplateSelect } from '../components/documents/TemplateSelects';
import { EmailPreview } from '../components/documents/EmailPreview';
import { InvoiceFormat2 } from '../components/documents/InvoiceFormat2';
import { InvoicePreview, orderRef } from '../components/documents/InvoicePreview';
import { customerChoices, engagementChoices, purchaseOrderChoices, useFreshRows, type ListedRecord } from '../lib/select-choices';
import { bankLinesOf, chosenBank, type BankAccount } from '../lib/bank';

interface Line {
  description: string;
  qty: number;
  unitPounds: string;
  amountPence: number;
  source: string;
  poId: string;
  poLineId: string;
  chargeLineIds: string[];
}

interface TemplateRow {
  id: string;
  name: string;
  active?: boolean;
  recurrence?: string;
  nextOn?: string;
  onRun?: string;
  lastError?: string;
  lastInvoiceId?: string;
  lastRunOn?: string;
}

interface Named extends ListedRecord {
  kind?: string;
  billToName?: string;
  billToAddress?: string;
  billToEmail?: string;
}

interface PoLineOption {
  id: string;
  reference: string;
  description: string;
  amountPence: number;
  remainingPence?: number;
}

interface PoOption extends Named {
  currency?: string;
  lines?: PoLineOption[];
}

function chargeIds(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : [];
}

export function InvoiceTemplatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const feedback = useNotice();
  const { can } = useAuth();
  const [error, setError] = useState('');
  const [row, setRow] = useState<TemplateRow | null>(null);
  const [name, setName] = useState('');
  const [active, setActive] = useState(false);
  const [recurrence, setRecurrence] = useState('off');
  const [runDay, setRunDay] = useState('1');
  const [nextOn, setNextOn] = useState('');
  const [onRun, setOnRun] = useState('draft');
  const [sendEmail, setSendEmail] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [engagementId, setEngagementId] = useState('');
  const [chargeLineId, setChargeLineId] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [issuedOn, setIssuedOn] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [title, setTitle] = useState('');
  const [docKind, setDocKind] = useState('');
  const [validDays, setValidDays] = useState('');
  const [vat, setVat] = useState('');
  const [layout, setLayout] = useState<'1' | '2'>('2');
  const [billToName, setBillToName] = useState('');
  const [billToAddress, setBillToAddress] = useState('');
  const [billToEmail, setBillToEmail] = useState('');
  const [billToDetails, setBillToDetails] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [contactName, setContactName] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [notice, setNotice] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [delivery, setDelivery] = useState('');
  const [acceptance, setAcceptance] = useState('');
  const [purpose, setPurpose] = useState('');
  const [scope, setScope] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [assist, setAssist] = useState<'create' | 'edit' | null>(null);
  const { previewRef, onFormScroll } = useLinkedPreview();
  const [customers, reloadCustomers] = useFreshRows<Named>('/api/customers');
  const [engagements, reloadEngagements] = useFreshRows<Named>('/api/engagements');
  const [chargeLines, setChargeLines] = useState<Named[]>([]);
  const [pos, reloadPos] = useFreshRows<PoOption>('/api/purchase-orders');
  const markCleanRef = useRef<() => void>(() => undefined);

  function applyRow(record: Record<string, unknown>) {
    setRow({
      id: String(record.id || ''),
      name: String(record.name || ''),
      lastError: record.lastError ? String(record.lastError) : undefined,
      lastInvoiceId: record.lastInvoiceId ? String(record.lastInvoiceId) : undefined,
      lastRunOn: record.lastRunOn ? String(record.lastRunOn) : undefined,
    });
    setName(String(record.name || ''));
    setCustomerId('');
    setActive(record.active === true);
    setRecurrence(String(record.recurrence || 'off'));
    setRunDay(String(record.runDay || 1));
    setNextOn(String(record.nextOn || ''));
    setOnRun(record.onRun === 'issue' ? 'issue' : 'draft');
    setSendEmail(record.sendEmail === true);
    setEngagementId(String(record.engagementId || ''));
    setChargeLineId(String(record.chargeLineId || ''));
    setCurrency(String(record.currency || 'GBP').toUpperCase());
    setIssuedOn(String(record.issuedOn || ''));
    setPeriodStart(String(record.periodStart || ''));
    setPeriodEnd(String(record.periodEnd || ''));
    setDueOn(String(record.dueOn || ''));
    setTitle(String(record.title || ''));
    setDocKind(String(record.docKind || ''));
    setValidDays(record.validDays == null ? '' : String(record.validDays));
    const nextLines = ((record.lines as Record<string, unknown>[]) || []).map((line) => ({
      description: String(line.description || ''),
      qty: Number(line.qty || 0),
      unitPounds: penceToPoundsInput(line.unitPence),
      amountPence: Number(line.amountPence || 0),
      source: String(line.source || 'manual'),
      poId: String(line.poId || ''),
      poLineId: String(line.poLineId || ''),
      chargeLineIds: chargeIds(line.chargeLineIds),
    }));
    setLines(nextLines);
    const net = nextLines.reduce((sum, line) => sum + line.amountPence, 0) || Number(record.amountPence || 0);
    setVat(vatRateValue(net, typeof record.vatPence === 'number' ? record.vatPence : null));
    setLayout(record.layout === '1' ? '1' : '2');
    setBillToName(String(record.billToName || ''));
    setBillToAddress(String(record.billToAddress || ''));
    setBillToEmail(String(record.billToEmail || ''));
    setBillToDetails(String(record.billToDetails || ''));
    setBankAccountId(String(record.bankAccountId || ''));
    setContactName(String(record.contactName || ''));
    setContactRole(String(record.contactRole || ''));
    setContactEmail(String(record.contactEmail || ''));
    setContactPhone(String(record.contactPhone || ''));
    setNotice(String(record.notice || ''));
    setPaymentTerms(String(record.paymentTerms || ''));
    setDelivery(String(record.delivery || ''));
    setAcceptance(String(record.acceptance || ''));
    setPurpose(String(record.purpose || ''));
    setScope(String(record.scope || ''));
    setEmailSubject(String(record.emailSubject || ''));
    setEmailBody(String(record.emailBody || ''));
  }

  useEffect(() => {
    void apiGet<Named[]>('/api/charge-lines').then(setChargeLines).catch(() => setChargeLines([]));
    void apiGet<BankAccount[]>('/api/bank-accounts').then((list) => setBankAccounts(list || [])).catch(() => setBankAccounts([]));
    if (!id || id === 'new') {
      setRow(null);
      setName('');
      setActive(false);
      setRecurrence('off');
      setRunDay('1');
      setNextOn('');
      setOnRun('draft');
      setSendEmail(false);
      setCustomerId('');
      setEngagementId('');
      setChargeLineId('');
      setCurrency('GBP');
      setIssuedOn('');
      setPeriodStart('');
      setPeriodEnd('');
      setDueOn('');
      setTitle('');
      setDocKind('');
      setValidDays('');
      setVat('');
      setLayout('2');
      setBillToName('');
      setBillToAddress('');
      setBillToEmail('');
      setBillToDetails('');
      setBankAccountId('');
      setContactName('');
      setContactRole('');
      setContactEmail('');
      setContactPhone('');
      setNotice('');
      setPaymentTerms('');
      setDelivery('');
      setAcceptance('');
      setPurpose('');
      setScope('');
      setEmailSubject('');
      setEmailBody('');
      setLines([{ description: '', qty: 1, unitPounds: '', amountPence: 0, source: 'manual', poId: '', poLineId: '', chargeLineIds: [] }]);
      return;
    }
    void apiGet<Record<string, unknown>>(`/api/invoice-templates/${id}`).then(applyRow).catch((err) => setError(apiError(err, 'Could not load')));
  }, [id]);

  useEffect(() => {
    if (customerId || !engagementId) return;
    const hit = engagements.find((item) => item.id === engagementId);
    if (hit?.customerId) setCustomerId(hit.customerId);
  }, [engagements, engagementId, customerId]);

  const engagementsForCustomer = engagements.filter((item) => !customerId || item.customerId === customerId);
  const linesForEngagement = chargeLines.filter((item) => !engagementId || item.engagementId === engagementId);
  const posForCustomer = pos.filter((item) => !customerId || item.customerId === customerId);
  const customer = customers.find((item) => item.id === customerId);
  const previewLines = lines.map((line) => {
    const order = pos.find((item) => item.id === line.poId);
    const ref = order?.lines?.find((item) => item.id === line.poLineId)?.reference || '';
    return {
      qty: line.qty,
      description: line.description,
      unitPence: poundsToPence(line.unitPounds),
      amountPence: line.amountPence,
      orderRef: orderRef(order?.poNumber, ref),
    };
  });
  const total = lines.reduce((sum, line) => sum + line.amountPence, 0);
  const vatPence = vatPenceOnLines(lines.map((line) => line.amountPence), vat);

  function payload() {
    return {
      name,
      active,
      recurrence,
      runDay: Number(runDay || 1),
      nextOn,
      onRun,
      sendEmail,
      engagementId,
      chargeLineId,
      currency,
      issuedOn: issuedOn || undefined,
      periodStart,
      periodEnd,
      dueOn: dueOn || undefined,
      title,
      docKind: docKind || undefined,
      validDays: validDays === '' ? undefined : Number(validDays),
      vatPence,
      ...(vatPence == null ? {} : { netPence: total }),
      layout,
      billToName, billToAddress, billToEmail, billToDetails,
      bankAccountId,
      contactName, contactRole, contactEmail, contactPhone,
      notice, paymentTerms, delivery, acceptance, purpose, scope,
      emailSubject, emailBody,
      lines: lines.map((line) => ({
        description: line.description,
        qty: Number(line.qty || 0),
        unitPence: poundsToPence(line.unitPounds),
        amountPence: line.amountPence,
        source: line.source,
        poId: line.poId,
        poLineId: line.poLineId,
        chargeLineIds: line.chargeLineIds,
      })),
    };
  }

  async function save() {
    if (!name.trim()) {
      feedback.failure('Template needs a name.');
      return;
    }
    setError('');
    try {
      const existing = row?.id || '';
      const saved = existing
        ? await apiPut<Record<string, unknown>>(`/api/invoice-templates/${existing}`, payload())
        : await apiPost<Record<string, unknown>>('/api/invoice-templates', payload());
      applyRow(saved);
      markCleanRef.current();
      if (!existing) navigate(`/invoice-templates/${saved.id}`);
      feedback.success('Saved');
    } catch (err) {
      feedback.failure(apiError(err, 'Save failed'));
    }
  }

  markCleanRef.current = useEditGuard(loadedRecordKey(id, row?.id), JSON.stringify(payload()), save);

  async function runNow() {
    if (!row?.id) return;
    const ok = await feedback.confirm('Run template', 'This creates the invoice for the current period. Issue also emails, files, and sends it to the books when that is selected.');
    if (!ok) return;
    setError('');
    try {
      const saved = await apiPost<Record<string, unknown>>(`/api/invoice-templates/${row.id}/run`, {});
      applyRow(saved);
      markCleanRef.current();
      feedback.success(onRun === 'issue' ? 'Invoice issued' : 'Draft created');
    } catch (err) {
      feedback.failure(apiError(err, 'Run failed'));
    }
  }

  async function remove() {
    if (!row?.id) return;
    const ok = await feedback.confirm('Delete template', 'This removes the template. Invoices already created stay.');
    if (!ok) return;
    try {
      await apiDelete(`/api/invoice-templates/${row.id}`);
      navigate('/invoice-templates');
      feedback.success('Deleted');
    } catch (err) {
      feedback.failure(apiError(err, 'Delete failed'));
    }
  }

  return (
    <div className="space-y-4">
      {assist ? <RecordAssistDialog kind="invoice-template" mode={assist} recordId={row?.id ? String(row.id) : undefined} current={payload()} title="Invoice template" onClose={() => setAssist(null)} onSaved={(saved) => { const next = String(saved.id || ''); if (next && !row?.id) navigate(`/invoice-templates/${next}`); else void apiGet<Record<string, unknown>>(`/api/invoice-templates/${next}`).then(applyRow); }} /> : null}
      {error ? <Alert error>{error}</Alert> : null}
      {row?.lastError ? <Alert error>{row.lastError}</Alert> : null}
      <PageHead
        stepper={id ? <RecordStepper listUrl="/api/invoice-templates" id={id} basePath="/invoice-templates" /> : null}
        crumbs={[{ label: 'Invoice templates', to: '/invoice-templates' }, { label: row?.id ? name || 'Template' : 'New' }]}
        back={{ label: 'Back to templates', to: '/invoice-templates' }}
        title={row?.id ? name || 'Template' : 'Invoice template'}
        subtitle={row?.lastRunOn ? `Last ran ${row.lastRunOn}` : 'Not run yet'}
        actions={
          <div className="flex gap-2">
            {can('invoices', 'draft') && row?.id ? <EditWithAiButton kind="invoice-template" status="" onClick={() => setAssist('edit')} /> : null}
            {can('invoices', 'draft') && !row?.id ? <Button variant="outline" onClick={() => setAssist('create')}>Prepare with AI</Button> : null}
            {can('invoices', 'draft') ? <Button onClick={() => void save()}>Save</Button> : null}
            {row?.id && can('invoices', 'issue') ? <Button variant="outline" onClick={() => void runNow()}>Run now</Button> : null}
            {row?.lastInvoiceId ? <Link to={`/invoices/${row.lastInvoiceId}`}><Button variant="outline">Last invoice</Button></Link> : null}
            {row?.id && can('invoices', 'draft') ? <Button variant="outline" onClick={() => void remove()}>Delete</Button> : null}
            <Button variant="outline" onClick={() => setShowPreview((value) => !value)}>{showPreview ? 'Edit' : 'Preview'}</Button>
          </div>
        }
      />
      <div className={`grid gap-6 ${showPreview ? 'h-[calc(100vh-16rem)] items-stretch xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : 'items-start'}`}>
      <div className={`min-w-0 space-y-4 ${showPreview ? 'min-h-0 overflow-y-auto pr-1' : ''}`} onScroll={showPreview ? onFormScroll : undefined}>
      <SectionCard title="Schedule" hint="The first run uses Next run. After that, the date moves by the schedule and uses the day of the month. The period and expected pay date move by the same number of months. A failure stays on this period and tries again the next morning.">
        <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
          <Field label="Name"><Input value={name} onChange={(event) => setName(event.target.value)} /></Field>
          <Field label="Schedule">
            <Select value={recurrence} onChange={(event) => setRecurrence(event.target.value)}>
              <option value="off">Off</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </Select>
          </Field>
          <Field label="Day of month"><Input type="number" min={1} max={28} value={runDay} onChange={(event) => setRunDay(event.target.value)} /></Field>
          <Field label="Next run"><Input type="date" value={nextOn} onChange={(event) => setNextOn(event.target.value)} /></Field>
          <Field label="When it runs">
            <Select value={onRun} onChange={(event) => setOnRun(event.target.value)}>
              <option value="draft">Create a draft</option>
              <option value="issue">Issue it</option>
            </Select>
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />
            Active
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={sendEmail} onChange={(event) => setSendEmail(event.target.checked)} />
            Email the customer when issuing
          </label>
        </div>
      </SectionCard>
      <SectionCard title="Invoice" hint="These fields are copied onto the invoice. Amounts stay as entered here.">
        <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
          <Field label="Customer"><Select searchable choices={customerChoices(customers)} onOpen={reloadCustomers} value={customerId} onChange={(event) => {
            const nextId = event.target.value;
            setCustomerId(nextId);
            setEngagementId('');
            setChargeLineId('');
            const next = customers.find((item) => item.id === nextId);
            setBillToName(next?.contactName || '');
            setBillToAddress(next?.postalAddress || '');
            setBillToEmail(next?.billingEmails?.[0] || '');
          }} /></Field>
          <Field label="Engagement"><Select searchable choices={engagementChoices(engagementsForCustomer)} onOpen={reloadEngagements} value={engagementId} onChange={(event) => { setEngagementId(event.target.value); setChargeLineId(''); }} /></Field>
          <Field label="Charge line"><Select searchable value={chargeLineId} onChange={(event) => {
            const nextId = event.target.value;
            setChargeLineId(nextId);
            const line = linesForEngagement.find((item) => item.id === nextId);
            if (line?.billToName) setBillToName(line.billToName);
            if (line?.billToAddress) setBillToAddress(line.billToAddress);
            if (line?.billToEmail) setBillToEmail(line.billToEmail);
          }}><option value="">Select</option>{linesForEngagement.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</Select></Field>
          <Field label="Title"><Input value={title} onChange={(event) => setTitle(event.target.value)} /></Field>
          <Field label="Currency"><Select value={currency} onChange={(event) => setCurrency(event.target.value)}><option value="GBP">GBP</option><option value="EUR">EUR</option><option value="USD">USD</option></Select></Field>
          <Field label="Date"><Input type="date" value={issuedOn} onChange={(event) => setIssuedOn(event.target.value)} /></Field>
          <Field label="Period start"><Input type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} /></Field>
          <Field label="Period end"><Input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} /></Field>
          <Field label="Expected pay date"><Input type="date" value={dueOn} onChange={(event) => setDueOn(event.target.value)} /></Field>
          <Field label="Valid days"><Input type="number" value={validDays} onChange={(event) => setValidDays(event.target.value)} /></Field>
          <Field label="Kind">
            <Select value={docKind} onChange={(event) => setDocKind(event.target.value)}>
              <option value="">Select</option>
              <option value="services">Services</option>
              <option value="licence">Licence</option>
              <option value="support">Support</option>
              <option value="commercial">Commercial</option>
            </Select>
          </Field>
          <VatRateSelect value={vat} onChange={setVat} />
          <Field label="Format"><Select value={layout} onChange={(event) => setLayout(event.target.value === '1' ? '1' : '2')}><option value="2">Modern</option><option value="1">Traditional</option></Select></Field>
          <Field label="Bank details"><Select value={chosenBank(bankAccounts, bankAccountId)?.id || ''} onChange={(event) => setBankAccountId(event.target.value)}><option value="">Select</option>{bankAccounts.map((account) => <option key={account.id} value={account.id}>{account.domestic ? `${account.name} · default` : account.name}</option>)}</Select></Field>
        </div>
      </SectionCard>
      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title="Client details" hint="Picking a customer fills bill to, address, and email. A charge line with its own bill to replaces them.">
          <div className="space-y-3">
            <Field label="Bill to"><Input value={billToName} onChange={(event) => setBillToName(event.target.value)} /></Field>
            <Field label="Address"><GrowingText value={billToAddress} onChange={setBillToAddress} /></Field>
            <Field label="Email"><Input value={billToEmail} onChange={(event) => setBillToEmail(event.target.value)} /></Field>
            <Field label="Details"><GrowingText value={billToDetails} onChange={setBillToDetails} /></Field>
          </div>
        </SectionCard>
        <SectionCard title="Supplier details" hint="The contact printed on the invoice.">
          <div className="space-y-3">
            <SupplierTemplateSelect onApply={(contact) => { setContactName(contact.contactName); setContactRole(contact.contactRole); setContactEmail(contact.contactEmail); setContactPhone(contact.contactPhone); }} />
            <Field label="Contact"><Input value={contactName} onChange={(event) => setContactName(event.target.value)} /></Field>
            <Field label="Role"><Input value={contactRole} onChange={(event) => setContactRole(event.target.value)} /></Field>
            <Field label="Email"><Input value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} /></Field>
            <Field label="Telephone"><Input value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} /></Field>
          </div>
        </SectionCard>
      </div>
      <SectionCard title="Email" hint="This text is used when the schedule issues the invoice. {{number}} and {{po}} are filled when you pick a template.">
        <div className="space-y-3">
          <EmailTemplateSelect kind="invoice" values={{ number: 'Invoice', customer: customer?.name || '', title, pos: [...new Set(lines.map((line) => pos.find((item) => item.id === line.poId)?.poNumber || '').filter(Boolean))] }} onApply={(subject, body) => { setEmailSubject(subject); setEmailBody(body); }} />
          <Field label="Subject"><Input value={emailSubject} onChange={(event) => setEmailSubject(event.target.value)} /></Field>
          <Field label="Body"><GrowingText value={emailBody} onChange={setEmailBody} /></Field>
        </div>
      </SectionCard>
      <LineTable
        columns={['Description', 'PO / line', 'Qty', 'Unit', 'Amount']}
        count={lines.length}
        addLabel="Add line item"
        onAdd={() => setLines((current) => [...current, { description: '', qty: 1, unitPounds: '', amountPence: 0, source: 'manual', poId: current.at(-1)?.poId || '', poLineId: '', chargeLineIds: [] }])}
        onRemove={(index) => setLines((current) => current.filter((_, i) => i !== index))}
        render={(index) => {
          const line = lines[index];
          const order = posForCustomer.find((item) => item.id === line.poId);
          const orderLines = order?.lines || [];
          return [
            <Input key="description" value={line.description} onChange={(event) => setLines((current) => current.map((item, i) => i === index ? { ...item, description: event.target.value } : item))} />,
            <div key="po" className="flex min-w-44 flex-col gap-1">
              <Select searchable className="w-full md:w-44" aria-label="Purchase order" choices={purchaseOrderChoices(posForCustomer)} onOpen={reloadPos} value={line.poId} onChange={(event) => setLines((current) => current.map((item, i) => i === index ? { ...item, poId: event.target.value, poLineId: '', chargeLineIds: [] } : item))} />
              <Select searchable className="w-full md:w-52" aria-label="Purchase order line" choices={[{ value: '', label: 'Line' }, ...orderLines.map((item) => ({ value: item.id, code: item.reference, description: item.description || '' }))]} onOpen={reloadPos} value={line.poLineId} onChange={(event) => setLines((current) => current.map((item, i) => {
                if (i !== index) return item;
                const orderLine = orderLines.find((candidate) => candidate.id === event.target.value);
                return { ...item, poLineId: event.target.value, description: item.description || orderLine?.description || '' };
              }))} />
            </div>,
            <Input key="qty" className="w-full md:w-24" type="number" step="0.25" value={line.qty} onChange={(event) => setLines((current) => current.map((item, i) => i === index ? { ...item, qty: Number(event.target.value), amountPence: Math.round(Number(event.target.value || 0) * poundsToPence(item.unitPounds)) } : item))} />,
            <Input key="unit" className="w-full md:w-28" type="number" step="0.01" value={line.unitPounds} onChange={(event) => setLines((current) => current.map((item, i) => i === index ? { ...item, unitPounds: event.target.value, amountPence: Math.round(Number(item.qty || 0) * poundsToPence(event.target.value)) } : item))} />,
            <span key="amount" className="tabular-nums">{formatMoney(line.amountPence, order?.currency || currency)}</span>,
          ];
        }}
      />
      <SectionCard title="Commercial terms" hint="Agreed terms for this invoice.">
        <TermsTemplateSelect kind="invoice" onApply={(terms) => { setPaymentTerms(terms.paymentTerms); setDelivery(terms.delivery); setNotice(terms.notice); setAcceptance(terms.acceptance); }} />
        <div className="grid gap-3 lg:grid-cols-2">
          <Field label="Payment terms"><TextArea value={paymentTerms} onChange={(event) => setPaymentTerms(event.target.value)} /></Field>
          <Field label="Delivery"><TextArea value={delivery} onChange={(event) => setDelivery(event.target.value)} /></Field>
          <Field label="Notice / Terms"><TextArea value={notice} onChange={(event) => setNotice(event.target.value)} /></Field>
          <Field label="To proceed"><TextArea value={acceptance} onChange={(event) => setAcceptance(event.target.value)} /></Field>
          <Field label="Purpose"><TextArea value={purpose} onChange={(event) => setPurpose(event.target.value)} /></Field>
          <Field label="Scope"><TextArea value={scope} onChange={(event) => setScope(event.target.value)} /></Field>
        </div>
      </SectionCard>
      <div className="text-right text-sm text-[var(--bo-muted)]">Net {formatMoney(total, currency)} · VAT {vatPence == null ? 'NONE' : formatMoney(vatPence, currency)}</div>
      <div className="text-right text-2xl">{formatMoney(vatPence == null ? total : total + vatPence, currency)}</div>
      </div>
      {showPreview ? <div ref={previewRef} className="min-h-0 min-w-0 space-y-4 overflow-y-auto">
        <div className="rounded-lg bg-[var(--bo-preview)] p-3">
          {layout === '1' ? (
            <InvoicePreview
              customerName={customer?.name || ''}
              billingEmails={customer?.billingEmails || []}
              issuedOn={issuedOn}
              dueOn={dueOn}
              currency={currency}
              poNumber=""
              billToName={billToName}
              billToAddress={billToAddress}
              billToEmail={billToEmail}
              billToDetails={billToDetails}
              bankLines={bankLinesOf(chosenBank(bankAccounts, bankAccountId))}
              title={title}
              contactName={contactName}
              contactRole={contactRole}
              contactEmail={contactEmail}
              contactPhone={contactPhone}
              validDays={Number(validDays) || 0}
              paymentTerms={paymentTerms}
              delivery={delivery}
              acceptance={acceptance}
              purpose={purpose}
              scope={scope}
              notice={notice}
              vatPence={vatPence}
              lines={previewLines}
            />
          ) : (
            <InvoiceFormat2
              customerName={customer?.name || ''}
              billingEmails={customer?.billingEmails || []}
              issuedOn={issuedOn}
              dueOn={dueOn}
              currency={currency}
              poNumber=""
              billToName={billToName}
              billToAddress={billToAddress}
              billToEmail={billToEmail}
              billToDetails={billToDetails}
              bankLines={bankLinesOf(chosenBank(bankAccounts, bankAccountId))}
              title={title}
              contactName={contactName}
              contactRole={contactRole}
              contactEmail={contactEmail}
              contactPhone={contactPhone}
              validDays={Number(validDays) || 0}
              paymentTerms={paymentTerms}
              delivery={delivery}
              acceptance={acceptance}
              purpose={purpose}
              scope={scope}
              notice={notice}
              vatPence={vatPence}
              lines={previewLines}
            />
          )}
        </div>
        <EmailPreview recipients={[billToEmail, ...(customer?.billingEmails || [])].map((email) => email.trim()).filter((email, index, all) => email && all.indexOf(email) === index).join(', ')} subject={emailSubject} body={emailBody} senderName={contactName} senderTitle={contactRole} senderEmail={contactEmail} senderPhone={contactPhone} />
      </div> : null}
      </div>
    </div>
  );
}
